import React, { useState } from 'react';
import type { PlanosProject, HostingPlan, UserProfile } from '../types';
import { HOSTING_PLANS, TOUR_SUBSCRIPTION_TIERS } from '../types';
import { activatePlanosHosting, getDaysRemaining, formatDaysRemaining, useActiveSlotForPlanos } from '../services/hosting';
import { X, Zap, CheckCircle, AlertTriangle, Loader2, Globe, Star, Sparkles, Send, Gift, ArrowUpCircle } from 'lucide-react';
import type { CanUserPublishResult } from '../services/hosting';

interface HostingModalProps {
  project: PlanosProject;
  user: UserProfile;
  slotCheck?: CanUserPublishResult | null;
  onClose: () => void;
  onSuccess: (updates: Partial<PlanosProject>, creditsRemaining?: number) => void;
  onOpenCredits?: () => void;
  onOpenPlans?: () => void;
}

const HostingModal: React.FC<HostingModalProps> = ({ project, user, slotCheck, onClose, onSuccess, onOpenCredits, onOpenPlans }) => {
  const canUseSlot = Boolean(
    slotCheck && slotCheck.tier !== 'free' && slotCheck.allowed && slotCheck.activeCount < slotCheck.limit
  );
  const showGetSubscription = !canUseSlot && onOpenPlans;
  const tierName = slotCheck ? (TOUR_SUBSCRIPTION_TIERS.find((t) => t.tier === slotCheck.tier)?.name ?? 'Pro') : 'Pro';

  const isDraft = project.hosting_status !== 'active';
  const isExpired = project.hosting_status === 'expired';
  const isExpired7d = isExpired && project.hosting_plan === '7d';
  const daysRemaining = getDaysRemaining(project.hosting_expires_at);
  const isRenewal = project.hosting_status === 'active' && daysRemaining != null && daysRemaining > 0;

  const [selectedPlan, setSelectedPlan] = useState<HostingPlan>(isExpired7d ? '12mo' : isDraft ? '7d' : '12mo');
  const [processing, setProcessing] = useState(false);
  const [slotProcessing, setSlotProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState<{ expires_at?: string; credits_remaining?: number } | null>(null);

  const selectedPlanData = HOSTING_PLANS.find((p) => p.plan === selectedPlan)!;
  const slug = project.slug ?? project.id;

  const handlePublishWithSlot = async () => {
    setSlotProcessing(true);
    setError(null);
    const res = await useActiveSlotForPlanos(project.id, user.id);
    setSlotProcessing(false);
    if (res.success && res.expires_at) {
      setSuccess(true);
      setResult({ expires_at: res.expires_at, credits_remaining: user.credits });
      onSuccess({
        hosting_plan: 'slot',
        hosting_expires_at: res.expires_at,
        hosting_status: 'active',
        is_published: true,
      });
    } else {
      setError(res.error ?? 'Could not use active space');
    }
  };

  const handleActivate = async () => {
    setProcessing(true);
    setError(null);
    const res = await activatePlanosHosting(project.id, user.id, selectedPlan);

    if (res.success) {
      setSuccess(true);
      setResult(res);
      onSuccess(
        {
          hosting_plan: selectedPlan,
          hosting_expires_at: res.expires_at!,
          hosting_status: 'active',
          is_published: true,
        },
        res.credits_remaining!
      );
    } else {
      setError(res.error === 'INSUFFICIENT_CREDITS' ? 'Not enough credits. Please top up first.' : (res.error || 'Something went wrong'));
    }
    setProcessing(false);
  };

  const getTitle = () => {
    if (isExpired7d) return 'Upgrade Your Plan';
    if (isRenewal) return 'Extend Publishing';
    if (isExpired) return 'Republish Plan';
    return 'Publish Your Plan';
  };

  const getSubtitle = () => {
    if (isExpired7d) return 'Your 7-day teaser has ended — upgrade to keep it live';
    if (isRenewal) return `Currently live — ${formatDaysRemaining(daysRemaining!)} remaining`;
    if (isExpired) return 'Plan is offline. Republish to go live again.';
    return 'Go live and share your spatial map';
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 hover:bg-slate-800 rounded-lg transition-colors">
          <X size={18} className="text-slate-400" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF3B30] to-[#8E0000] flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-100">{getTitle()}</h3>
            <p className="text-xs text-slate-400">{getSubtitle()}</p>
          </div>
        </div>

        {success && result && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle size={20} />
              <span className="font-semibold text-sm">Your plan is now live!</span>
            </div>
            <div className="p-4 bg-slate-800 rounded-xl space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Live until</span>
                <span className="text-slate-100 font-medium">{new Date(result.expires_at!).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Share link</span>
                <span className="text-[#FF3B30] font-medium text-xs">{window.location.origin}/view/{slug}</span>
              </div>
            </div>
            <button onClick={onClose} className="w-full px-4 py-2.5 bg-[#FF3B30] hover:bg-[#e6352b] text-white rounded-lg text-sm font-semibold">
              Done
            </button>
          </div>
        )}

        {!success && (
          <div className="space-y-5">
            <div className="p-3 bg-slate-800/50 rounded-xl">
              <p className="text-sm font-medium text-slate-100">{project.title}</p>
              <p className="text-xs text-slate-400">{isRenewal ? `Live · Expires ${new Date(project.hosting_expires_at!).toLocaleDateString('en-GB')}` : isExpired ? 'Expired' : 'Draft'}</p>
            </div>

            {isDraft && (
              <div className="p-3 bg-green-900/15 border border-green-900/30 rounded-xl">
                <p className="text-xs text-green-400 font-medium">Building is free — only pay when you're ready to share</p>
              </div>
            )}

            {showGetSubscription && (
              <div className="p-4 bg-purple-900/15 border border-purple-500/30 rounded-xl">
                <p className="text-sm font-medium text-purple-300 mb-1">Get a subscription for active spaces</p>
                <p className="text-xs text-slate-400 mb-3">
                  Pro, Agency, and Business plans give you active spaces for 360 Tours and Planos — no credits per publish.
                </p>
                <button
                  type="button"
                  onClick={onOpenPlans}
                  className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <ArrowUpCircle size={16} />
                  View active space plans
                </button>
              </div>
            )}

            {canUseSlot && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-300">Publish using an active space (Free)</p>
                <p className="text-xs text-slate-400">
                  You have {slotCheck!.activeCount} of {slotCheck!.limit} active spaces used (360 Tours + Planos shared).
                </p>
                <button
                  onClick={handlePublishWithSlot}
                  disabled={slotProcessing}
                  className="w-full px-4 py-3 bg-green-600 hover:bg-green-500 disabled:bg-slate-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                >
                  {slotProcessing ? (
                    <><Loader2 size={16} className="animate-spin" /> Publishing...</>
                  ) : (
                    <><Gift size={16} /> Publish using an active space (Free)</>
                  )}
                </button>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">
                {canUseSlot ? 'Or publish using Credits (select duration)' : 'Publish using Credits (select duration)'}
              </label>
              {HOSTING_PLANS.filter((p) => p.plan !== '6mo').map((p) => {
                const isSelected = selectedPlan === p.plan;
                return (
                  <button
                    key={p.plan}
                    onClick={() => setSelectedPlan(p.plan)}
                    className={`w-full p-4 rounded-xl border text-left transition-all flex items-center justify-between ${
                      isSelected ? 'border-[#FF3B30] bg-[#FF3B30]/10 ring-1 ring-[#FF3B30]/30' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-[#FF3B30] bg-[#FF3B30]' : 'border-slate-600'}`}>
                        {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                      <span className="text-sm font-semibold text-slate-100">{p.label}</span>
                      {p.badge === 'most-popular' && <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-[10px] font-bold rounded">Popular</span>}
                      {p.badge === 'free' && <span className="px-2 py-0.5 bg-green-500/20 text-green-300 text-[10px] font-bold rounded">Free</span>}
                    </div>
                    <span className="text-[#FF3B30] font-bold">{p.credits} credits</span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Zap size={16} className="text-[#FF3B30]" />
                Your balance
              </div>
              <span className={`text-sm font-bold ${user.credits >= selectedPlanData.credits ? 'text-green-400' : 'text-red-400'}`}>{user.credits} credits</span>
            </div>

            {user.credits < selectedPlanData.credits && (
              <div className="p-3 bg-red-900/20 border border-red-900/50 rounded-xl flex items-start gap-2">
                <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
                <p className="text-xs text-red-300">
                  You need {selectedPlanData.credits - user.credits} more credits.{' '}
                  {onOpenCredits && (
                    <button onClick={onOpenCredits} className="underline font-semibold">Top up</button>
                  )}
                </p>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-900/20 border border-red-900/50 rounded-xl flex items-start gap-2">
                <AlertTriangle size={16} className="text-red-400" />
                <p className="text-xs text-red-300">{error}</p>
              </div>
            )}

            <button
              onClick={handleActivate}
              disabled={processing || user.credits < selectedPlanData.credits}
              className="w-full px-4 py-3 bg-[#FF3B30] hover:bg-[#e6352b] disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Send size={16} />
                  {isRenewal ? 'Extend' : isExpired ? 'Republish' : 'Publish'} — {selectedPlanData.credits} Credits
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HostingModal;
